import React, { useState } from 'react';
import {
  Box,
  Button,
  FormControl,
  FormLabel,
  Input,
  Flex,
  Heading,
  useToast,
  Text,
  Popover,
  PopoverTrigger,
  PopoverContent,
  PopoverHeader,
  PopoverBody,
  PopoverFooter,
  PopoverArrow,
  PopoverCloseButton,
  ButtonGroup,
  useDisclosure,
  Table,
  Thead,
  Tbody,
  Tr,
  Th,
  Td,
  Badge,
  HStack,
  useRadio,
  useRadioGroup
} from '@chakra-ui/react';
import { createManualOrder } from '../api';

// 自定义 RadioCard 组件
function RadioCard(props) {
  const { getInputProps, getRadioProps } = useRadio(props);
  const input = getInputProps();
  const checkbox = getRadioProps();

  // 根据值设置不同的颜色
  const bgColor = props.value === 'call' ? 'green.600' : 'red.600';
  const borderColor = props.value === 'call' ? 'green.600' : 'red.600';

  return (
    <Box as='label'>
      <input {...input} />
      <Box
        {...checkbox}
        cursor='pointer'
        borderWidth='1px'
        borderRadius='md'
        boxShadow='sm'
        _checked={{
          bg: bgColor,
          color: 'white',
          borderColor: borderColor,
        }}
        _focus={{
          boxShadow: 'outline',
        }}
        px={4}
        py={2}
        fontSize="sm"
        fontWeight="medium"
      >
        {props.children}
      </Box>
    </Box>
  );
}

const ManualOrder = () => {
  const [formData, setFormData] = useState({
    symbol: 'QQQ',
    option_type: 'call', // 默认为 call
  });
  const [isLoading, setIsLoading] = useState(false);
  const [orderResult, setOrderResult] = useState(null);
  const toast = useToast();
  const { isOpen, onOpen, onClose } = useDisclosure();

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData({ ...formData, [name]: value });
  };

  const handleOptionTypeChange = (value) => {
    setFormData({ ...formData, option_type: value });
  };

  const validateForm = () => {
    if (!formData.symbol) {
      toast({
        title: '请输入股票标的',
        status: 'error',
        duration: 3000,
        isClosable: true,
      });
      return false;
    }
    return true;
  };

  const handleSubmit = async () => {
    if (!validateForm()) return;
  };

  const confirmOrder = async () => {
    onClose();
    setIsLoading(true);
    try {
      const response = await createManualOrder(formData);
      setOrderResult(response);
      toast({
        title: '下单成功',
        description: `成功为 ${formData.symbol} 创建${formData.option_type === 'call' ? '看涨' : '看跌'}期权订单`,
        status: 'success',
        duration: 5000,
        isClosable: true,
      });
    } catch (error) {
      console.error('下单失败:', error);
      toast({
        title: '下单失败',
        description: error.response?.data?.detail || '服务器错误',
        status: 'error',
        duration: 5000,
        isClosable: true,
      });
    } finally {
      setIsLoading(false);
    }
  };

  const getOptionTypeBadge = (type) => {
    const colors = {
      'call': 'green',
      'put': 'red',
    };
    const text = {
      'call': '看涨',
      'put': '看跌',
    };
    return (
      <Badge colorScheme={colors[type]}>
        {text[type]}
      </Badge>
    );
  };

  // 使用 useRadioGroup 钩子来控制自定义单选按钮组
  const options = [
    { value: 'call', label: '看涨期权 (Call)' },
    { value: 'put', label: '看跌期权 (Put)' }
  ];

  const { getRootProps, getRadioProps } = useRadioGroup({
    name: 'option_type',
    value: formData.option_type,
    onChange: handleOptionTypeChange,
  });

  const group = getRootProps();

  return (
    <Box p={4}>
      <Box mb={6} borderWidth="1px" borderRadius="lg" overflow="hidden" bg="white">
        <Box p={4} borderBottomWidth="1px">
          <Heading size="md">创建新订单</Heading>
        </Box>
        <Box p={4}>
          <Flex direction="column" gap={4}>
            <FormControl isRequired>
              <FormLabel>股票标的</FormLabel>
              <Input 
                name="symbol" 
                value={formData.symbol} 
                onChange={(e) => setFormData({ ...formData, symbol: e.target.value.toUpperCase() })}
                placeholder="例如: AAPL"
                maxW="300px"
              />
            </FormControl>
            
            <FormControl>
              <FormLabel>期权类型</FormLabel>
              <HStack {...group} spacing={4}>
                {options.map((option) => {
                  const radio = getRadioProps({ value: option.value });
                  return (
                    <RadioCard key={option.value} {...radio} value={option.value}>
                      {option.label}
                    </RadioCard>
                  );
                })}
              </HStack>
            </FormControl>
            
            <Box mt={2}>
              <Popover
                isOpen={isOpen}
                onClose={onClose}
                placement="top"
                closeOnBlur={false}
              >
                <PopoverTrigger>
                  <Button 
                    colorScheme="blue" 
                    isLoading={isLoading} 
                    onClick={onOpen}
                    size="md"
                  >
                    提交订单
                  </Button>
                </PopoverTrigger>
                <PopoverContent>
                  <PopoverArrow />
                  <PopoverCloseButton />
                  <PopoverHeader fontWeight="semibold">确认订单</PopoverHeader>
                  <PopoverBody>
                    您确定要为 {formData.symbol} 创建{formData.option_type === 'call' ? '看涨' : '看跌'}期权订单吗？
                  </PopoverBody>
                  <PopoverFooter display="flex" justifyContent="flex-end">
                    <ButtonGroup size="sm">
                      <Button variant="outline" onClick={onClose}>取消</Button>
                      <Button colorScheme="blue" onClick={confirmOrder}>确认</Button>
                    </ButtonGroup>
                  </PopoverFooter>
                </PopoverContent>
              </Popover>
            </Box>
          </Flex>
        </Box>
      </Box>

      {orderResult && (
        <Box borderWidth="1px" borderRadius="lg" overflow="hidden" bg="white">
          <Box p={4} borderBottomWidth="1px">
            <Heading size="md">订单结果</Heading>
          </Box>
          <Box overflowX="auto">
            <Table variant="simple" size="md">
              <Thead>
                <Tr>
                  <Th>属性</Th>
                  <Th>值</Th>
                </Tr>
              </Thead>
              <Tbody>
                <Tr>
                  <Td>标的</Td>
                  <Td>{orderResult.symbol}</Td>
                </Tr>
                <Tr>
                  <Td>期权代码</Td>
                  <Td>{orderResult.option_symbol || '未提供'}</Td>
                </Tr>
                <Tr>
                  <Td>类型</Td>
                  <Td>{getOptionTypeBadge(orderResult.option_type)}</Td>
                </Tr>
                <Tr>
                  <Td>数量</Td>
                  <Td>{orderResult.quantity || '未提供'}</Td>
                </Tr>
                <Tr>
                  <Td>入场价格</Td>
                  <Td>{orderResult.entry_price || '未提供'}</Td>
                </Tr>
                <Tr>
                  <Td>状态</Td>
                  <Td>{orderResult.status || orderResult.success ? '成功' : '失败'}</Td>
                </Tr>
                {orderResult.error && (
                  <Tr>
                    <Td>错误</Td>
                    <Td color="red.500">{orderResult.error}</Td>
                  </Tr>
                )}
                {orderResult.message && (
                  <Tr>
                    <Td>消息</Td>
                    <Td>{orderResult.message}</Td>
                  </Tr>
                )}
              </Tbody>
            </Table>
          </Box>
        </Box>
      )}
    </Box>
  );
};

export default ManualOrder; 